import { SitkaModule } from "olio-sitka"
import { put, select, call, all } from "redux-saga/effects"
import * as actions from "../action_creators"
import {
    GET_GOOGLE_CALENDAR_CONFIGS,
    GET_GOOGLE_CALENDAR_CONFIG,
    GET_GOOGLE_CALENDAR_CONFIG_TAGS,
    ACCOUNTS,
    AVAILABLE_CALENDARS,
    buildCalendarQuery,
    TAG_KEYWORDS,
    ATTENDEE_USERS,
} from "../../graphql_queries/goolge_calendar_config_queries"
import { sendGraphqlRequest } from "../../api/graphQlClient"
import {
    removeCalendarSubscription,
    setPendingConfigTeamId,
    computeNewCalendarConfigState,
    setSelectedGoogleCalendarConfigId,
    ingressNewConfigToState,
    addNewAccountsToConfigState,
    setConfigDescription,
    updateTagKeywords,
    updateDefaultTags,
    sortTagKeywords,
    sortAttendeeUsers,
    updateAttendeeUsers,
    setConfigCreateRecordsForMembers,
    setConfigCreateAllDayRecords,
    putConfigMeta,
    sortNewAndExistingTags,
    sortNewAndExistingKeywordTags,
    deleteCalendarConfigFromState,
} from "../../util/google_calendar_config_utils"
import { createOAuthPopupWindow } from "../../util/oauth_utils"
import * as client from "../../api/client"
import { makeToast } from "../../util/toast_util"
import { appStrings } from "../../common/strings/app_strings"

const {
    googleCalendarAccountAdded,
    googleCalendarConfigAdded,
    googleCalendarSubscriptionAdded,
    accountRemoved,
    calendarRemoved,
    configUpdated,
    keywordsAdded,
} = appStrings.connectedApps

export default class GoogleCalendarConfigsModule extends SitkaModule {
    moduleName = "googleCalendarConfigs"
    defaultState = {
        items: {},
        sort: [],
        selected: null,
        pendingConfigTeamId: null,
        saveInProgress: false,
    }

    constructor() {
        super()
        this.updateState = this.updateState.bind(this)
        this.setSaveInProgress = this.setSaveInProgress.bind(this)
    }

    *handleNewGoogleToken(oauthTokenId) {
        const currentState = yield select(this.getCurrentState)
        const { pendingConfigTeamId, selected } = currentState

        // create new google calendar config for pending team id
        if (pendingConfigTeamId) {
            // create google calendar config with oauth token ID & team ID
            const newConfigMeta = {
                teamId: pendingConfigTeamId,
            }
            try {
                // post new config
                const resp = yield call(
                    client.postGoogleCalendarConfig,
                    newConfigMeta,
                    oauthTokenId,
                )
                const { id } = resp
                makeToast(googleCalendarConfigAdded, "success")

                // fetch graphql config
                yield SitkaModule.callAsGenerator(
                    this.handleFetchGoogleCalendarConfigById,
                    id,
                )

                // set selected config
                yield SitkaModule.callAsGenerator(
                    this.handleSelectGoogleCalendarConfig,
                    id,
                )
            } catch (e) {
                const errorMsg =
                    "Failed to create new google calendar config. Please try again"
                yield call(makeToast, errorMsg, "danger")
            }
            return
        }

        // add new google calendar account to selected account
        if (selected) {
            const newAccountMeta = {
                oauthTokenId,
            }
            try {
                const resp = yield call(
                    client.postGoogleCalendarAccount,
                    newAccountMeta,
                    selected,
                )
                if (resp) {
                    makeToast(googleCalendarAccountAdded, "success")
                    // fetch new accounts for selected config
                    const request = buildCalendarQuery(selected, [
                        ACCOUNTS,
                        AVAILABLE_CALENDARS,
                    ])
                    const { gCalendarConfig: resp } = yield call(
                        sendGraphqlRequest,
                        request,
                    )
                    const { gCalendarAccounts, availableCalendars } = resp[0]
                    // update state with new accounts
                    const newState = addNewAccountsToConfigState(
                        currentState,
                        selected,
                        gCalendarAccounts,
                        availableCalendars,
                    )
                    yield call(this.updateState, { ...newState })
                }
            } catch (e) {
                const errorMsg =
                    "Failed to add Google Calendar account. Confirm that account is not already linked or try again."
                yield call(makeToast, errorMsg, "danger")
            }
        }
    }

    *handleFetchGoogleCalendarConfigById(id) {
        if (!id) {
            return
        }
        try {
            const { gCalendarConfig: resp } = yield call(
                sendGraphqlRequest,
                GET_GOOGLE_CALENDAR_CONFIG(id),
            )
            const originalState = yield select(this.getCurrentState)
            if (resp.length > 0) {
                const newConfig = resp[0]
                const newState = ingressNewConfigToState(
                    originalState,
                    newConfig,
                )
                yield call(this.updateState, { ...newState })
            }
        } catch (e) {
            const errorMsg =
                "Something went wrong fetching google calendar config: " + id
            yield call(makeToast, errorMsg, "danger")
        }
    }

    *handleFetchGoogleCalendarConfigs() {
        try {
            const { gCalendarConfig: resp } = yield call(
                sendGraphqlRequest,
                GET_GOOGLE_CALENDAR_CONFIGS,
            )
            const originalState = yield select(this.getCurrentState)
            const newState = computeNewCalendarConfigState(originalState, resp)
            yield call(this.updateState, { ...newState })
        } catch (e) {
            const errorMsg =
                "Something went wrong fetching google calendar configs."
            yield call(makeToast, errorMsg, "danger")
        }
    }

    *handleRemoveCalendar(calendarId, configId) {
        try {
            const success = yield call(
                client.deleteGoogleCalendarSubscription,
                calendarId,
            )
            if (!success) {
                throw "failed"
            }
            const state = yield select(this.getCurrentState)
            const newState = removeCalendarSubscription(
                state,
                configId,
                calendarId,
            )
            yield call(this.updateState, { ...newState })
            makeToast(calendarRemoved, "success")
        } catch (e) {
            const errorMsg =
                "Something went wrong unsubscribing from calendar id: " +
                calendarId
            yield call(makeToast, errorMsg, "danger")
        }
    }

    *handleRemoveAccount(accountId, configId) {
        if (!accountId || !configId) {
            return
        }
        try {
            yield call(this.setSaveInProgress, true)
            const success = yield call(
                client.deleteGoogleCalendarAccount,
                accountId,
            )
            if (!success) {
                throw "failed"
            }
            // fetch config with updated accounts & calendar subscriptions
            yield SitkaModule.callAsGenerator(
                this.handleFetchGoogleCalendarConfigById,
                configId,
            )
            makeToast(accountRemoved, "success")
        } catch (e) {
            const errorMsg =
                "Something went wrong removing account id: " + accountId
            yield call(makeToast, errorMsg, "danger")
        } finally {
            yield call(this.setSaveInProgress, false)
        }
    }

    *handleSelectGoogleCalendarConfig(id) {
        const state = yield select(this.getCurrentState)

        const newState = setSelectedGoogleCalendarConfigId(state, id)
        yield call(this.updateState, { ...newState })
    }

    *handleSetPendingConfigTeamId(id) {
        const state = yield select(this.getCurrentState)
        const newState = setPendingConfigTeamId(state, id)

        yield call(this.updateState, { ...newState })
    }

    *handleStartGoogleOauth() {
        const childWindow = createOAuthPopupWindow()
        const service = "google"
        yield call(this.modules.oauth.handleStartOAuth, childWindow, service)
    }

    *handleUpdateConfigDescription(defaultDescription, configId) {
        const meta = {
            id: configId,
            defaultDescription,
        }
        const isSuccessful = yield SitkaModule.callAsGenerator(
            this.handlePutGoogleCalendarConfig,
            meta,
        )
        if (isSuccessful) {
            const state = yield select(this.getCurrentState)
            const newState = setConfigDescription(
                state,
                configId,
                defaultDescription,
            )
            yield call(this.updateState, { ...newState })
        }
    }

    *handleUpdateCreateRecordsForMembers(createRecordsForMembers, configId) {
        const meta = {
            id: configId,
            createRecordsForMembers,
        }
        const isSuccessful = yield SitkaModule.callAsGenerator(
            this.handlePutGoogleCalendarConfig,
            meta,
        )
        if (isSuccessful) {
            const state = yield select(this.getCurrentState)
            const newState = setConfigCreateRecordsForMembers(
                state,
                createRecordsForMembers,
                configId,
            )
            yield call(this.updateState, { ...newState })
        }
    }

    *handleUpdateCreateAllDayRecords(createAllDayRecords, configId) {
        const meta = {
            id: configId,
            createAllDayRecords,
        }
        const isSuccessful = yield SitkaModule.callAsGenerator(
            this.handlePutGoogleCalendarConfig,
            meta,
        )
        if (isSuccessful) {
            const state = yield select(this.getCurrentState)
            const newState = setConfigCreateAllDayRecords(
                state,
                createAllDayRecords,
                configId,
            )
            yield call(this.updateState, { ...newState })
        }
    }

    // returns new Id for tag
    *handlePostConfigTag(tag) {
        yield call(this.setSaveInProgress, true)
        const resp = yield call(client.postTag, tag)
        yield put(actions.receiveTag(resp))
        yield call(this.setSaveInProgress, false)
        return resp.id
    }

    // returns default tagId array
    *handleCreateNewTagsFromConfigTags(tagsArray, teamId) {
        const { newTags, existingTagIds } = sortNewAndExistingTags(tagsArray)

        // get ids for all new tags
        const newTagIds = yield all(newTags.map(tag => {
            return SitkaModule.callAsGenerator(this.handlePostConfigTag, { ...tag, teamId })
        }))

        // combine new and existing tag ids
        const defaultTagIds = [...newTagIds, ...existingTagIds]
        return defaultTagIds
    }

    *handleUpdateConfigTags(configId, tagsArray) {
        if (!configId || !tagsArray) {
            return
        }
        const currentState = yield select(this.getCurrentState)
        const { teamId } = currentState.items[configId]

        const defaultTagIds = yield SitkaModule.callAsGenerator(this.handleCreateNewTagsFromConfigTags, tagsArray, teamId)

        const meta = {
            id: configId,
            defaultTagIds,
        }
        const isSuccessful = yield SitkaModule.callAsGenerator(
            this.handlePutGoogleCalendarConfig,
            meta,
        )
        if (!isSuccessful) {
            // error message handled in handlePutGoogleCalendarConfig
            return
        }
        try {
            const { gCalendarConfig: resp } = yield call(
                sendGraphqlRequest,
                GET_GOOGLE_CALENDAR_CONFIG_TAGS(configId),
            )
            const { defaultTags } = resp[0]

            // update state with new accounts
            const newState = updateDefaultTags(
                currentState,
                configId,
                defaultTags,
            )
            yield call(this.updateState, { ...newState })
            return
        } catch (e) {
            const errorMsg = `Something went wrong fetching tags for config: ${meta.id}`
            yield call(makeToast, errorMsg, "danger")
        }
    }

    *handlePutGoogleCalendarConfig(patchMeta) {
        const state = yield select(this.getCurrentState)
        const config = state.items[patchMeta.id]

        try {
            yield call(this.setSaveInProgress, true)
            const meta = putConfigMeta(config, patchMeta)
            yield call(client.putGoogleCalendarConfig, meta)
            makeToast(configUpdated, "success")
        } catch (e) {
            const errorMsg = `Something went wrong updating config: ${patchMeta.id}`
            yield call(makeToast, errorMsg, "danger")
            return false
        } finally {
            yield call(this.setSaveInProgress, false)
        }
        return true
    }

    *handleNewConfig(config) {
        // todo: handle new config
    }

    *setSaveInProgress(saveInProgress) {
        const currentState = yield select(this.getCurrentState)
        const newState = { ...currentState, saveInProgress }
        yield call(this.updateState, newState)
    }

    *handleAddNewCalendar(configId, gCalendarAccountId, availableCalendarId) {
        if (!gCalendarAccountId || !configId || !availableCalendarId) {
            return
        }
        try {
            yield call(this.setSaveInProgress, true)
            const meta = {
                googleCalendarAccountId: gCalendarAccountId,
                googleCalendarId: availableCalendarId,
            }

            yield call(client.postGoogleCalendarSubscription, meta)

            // fetch config with updated accounts & calendar subscriptions
            yield SitkaModule.callAsGenerator(
                this.handleFetchGoogleCalendarConfigById,
                configId,
            )
            makeToast(googleCalendarSubscriptionAdded, "success")
        } catch (e) {
            const errorMsg =
                "Something went wrong subscribing to calendar " +
                availableCalendarId
            yield call(makeToast, errorMsg, "danger")
        } finally {
            yield call(this.setSaveInProgress, false)
        }
    }

    // returns keyword with id for new tag
    *handlePostConfigTagForKeywordTag(keywordTag, teamId) {
        const tagId = yield SitkaModule.callAsGenerator(this.handlePostConfigTag, { ...keywordTag.tag, teamId })
        return { ...keywordTag, tagId, tag: { ...keywordTag.tag, id: tagId } }
    }

    // returns all keywords with updated team ids 
    *handleCreateNewTagsFromConfigKeywords(tagKeywordsArray, teamId) {
        const { usesNewTag, usesExistingTag } = sortNewAndExistingKeywordTags(tagKeywordsArray)

        // get ids for all new tags
        const updatedKeywords = yield all(usesNewTag.map(keyword => {
            return SitkaModule.callAsGenerator(this.handlePostConfigTagForKeywordTag, keyword, teamId)
        }))

        // combine new and existing tag ids
        const tagKeywords = [...updatedKeywords, ...usesExistingTag]
        return tagKeywords
    }

    *handleUpdateTagKeywords(rawTagKeywords, configId) {
        const state = yield select(this.getCurrentState)
        const config = state.items[configId]
        if (!config) {
            return
        }

        const newTagKeywords = yield SitkaModule.callAsGenerator(this.handleCreateNewTagsFromConfigKeywords, rawTagKeywords, config.teamId)

        const {
            newKeywords,
            updatedKeywords,
            keywordIdsToDelete,
        } = sortTagKeywords(config, newTagKeywords)

        try {
            yield call(this.setSaveInProgress, true)
            // post new tag-keywords
            yield all(
                newKeywords.map(meta => {
                    return call(client.postCalendarKeywordTag, meta)
                }),
            )
            // put tag-keywords to update
            yield all(
                updatedKeywords.map(keyword => {
                    return call(
                        client.putCalendarKeywordTag,
                        keyword,
                        keyword.id,
                    )
                }),
            )
            // delete tag-keywords
            yield all(
                keywordIdsToDelete.map(id => {
                    return call(client.deleteCalendarKeywordTag, id)
                })
            )

            const request = buildCalendarQuery(configId, [TAG_KEYWORDS])
            const { gCalendarConfig: resp } = yield call(
                sendGraphqlRequest,
                request,
            )
            const { tagKeywords } = resp[0]
            const newState = updateTagKeywords(state, tagKeywords, configId)
            makeToast(keywordsAdded, "success")

            yield call(this.updateState, { ...newState })
        } catch (e) {
            const errorMsg = "Something went wrong updating tags"
            yield call(makeToast, errorMsg, "danger")
        } finally {
            yield call(this.setSaveInProgress, false)
        }
    }

    *handleUpdateAttendeeUsers(newAttendeeUsers, configId) {
        const state = yield select(this.getCurrentState)
        const config = state.items[configId]
        if (!config) {
            return
        }

        const {
            newMatches,
            matchesToUpdate,
            matchesToDelete,
        } = sortAttendeeUsers(config, newAttendeeUsers)

        try {
            yield call(this.setSaveInProgress, true)

            // post new attendee-users
            yield all(
                newMatches.map(meta => {
                    return call(client.postCalendarAttendeeUser, meta)
                }),
            )
            //  update attendee-users
            yield all(
                matchesToUpdate.map(keyword => {
                    return call(
                        client.putCalendarAttendeeUser,
                        keyword,
                        keyword.id,
                    )
                }),
            )
            // delete attendee-users
            yield all(
                matchesToDelete.map(id => {
                    return call(client.deleteCalendarAttendeeUser, id)
                }),
            )
            const request = buildCalendarQuery(configId, [ATTENDEE_USERS])
            const { gCalendarConfig: resp } = yield call(
                sendGraphqlRequest,
                request,
            )
            const { attendeeUsers } = resp[0]
            const newState = updateAttendeeUsers(state, attendeeUsers, configId)
            makeToast("Attendee - Users updated", "success")

            yield call(this.updateState, { ...newState })
        } catch (e) {
            const errorMsg = "Something went wrong updating attendees"
            yield call(makeToast, errorMsg, "danger")
        } finally {
            yield call(this.setSaveInProgress, false)
        }
    }

    *handleDeleteConfig(configId) {
        try {
            yield call(this.setSaveInProgress, true)
            yield call(
                client.deleteGoogleCalendarConfig,
                configId,
            )
            const state = yield select(this.getCurrentState)
            const newState = deleteCalendarConfigFromState(state, configId)
            yield call(this.updateState, { ...newState })
            yield call(makeToast, "The Google Calendar Configuration has been deleted and all related accounts have been disconnected.", "success")
        } catch (e) {
            yield call(makeToast, "Something went deleting Google Calendar Config. Please try Again.", "danger")
        } finally {
            yield call(this.setSaveInProgress, false)
        }
    }

    *updateState(newState) {
        yield put(
            this.setState({
                ...(yield select(this.getCurrentState)),
                ...newState,
            }),
        )
    }

    getCurrentState(state) {
        return state.googleCalendarConfigs
    }

    getAppState(state) {
        return state
    }
}



// WEBPACK FOOTER //
// ./src/redux/modules/google_calendar_configs.js